import React, { useMemo, lazy, Suspense } from 'react';
import { BADGES } from '../utils/data';
import { format, isValid } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { shareProgress } from '../utils/share';

const { FiClock, FiCheckCircle, FiTrendingUp, FiAward, FiShare2, FiBarChart2 } = FiIcons;

// Lazy load the chart component for better initial load
const ReactECharts = lazy(() => import('echarts-for-react'));

const ChartSkeleton = () => (
  <div className="h-[180px] w-full bg-slate-50 rounded-2xl animate-pulse flex items-center justify-center">
    <SafeIcon icon={FiBarChart2} className="text-slate-200 text-4xl" />
  </div>
);

export const StatsChart = ({ history = [], weightLogs = [], streak = 0, longestFast = 0, units = 'metric' }) => {
  const safeFormat = (dateStr, formatStr) => {
    try {
      const d = new Date(dateStr);
      return isValid(d) ? format(d, formatStr) : '--';
    } catch (e) {
      return '--';
    }
  };

  const handleShareHistory = (entry) => {
    const text = `Just finished a ${entry.duration.toFixed(1)}h fast on KetoMachine! 🏆 Target was ${entry.target}h. #KetoMachine #FastingVictory`;
    shareProgress({ title: 'Fast Completed!', text });
  };

  // Memoize expensive calculations
  const getLast7DaysData = useMemo(() => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const data = new Array(7).fill(0);
    
    if (Array.isArray(history)) {
      history.slice(0, 10).forEach(entry => {
        const d = new Date(entry.end);
        if (isValid(d)) {
          data[d.getDay()] += entry.duration || 0;
        }
      });
    }
    
    return {
      labels: days,
      values: data.map(v => parseFloat(v.toFixed(1)))
    };
  }, [history]);

  const processedWeightData = useMemo(() => {
    return weightLogs.map(l => ({
      date: safeFormat(l.date, 'MMM d'),
      value: parseFloat((units === 'imperial' ? l.value * 2.20462 : l.value).toFixed(1))
    }));
  }, [weightLogs, units]);

  const earnedBadges = useMemo(() => {
    return BADGES.filter(badge => {
      if (badge.reqHours && longestFast >= badge.reqHours) return true;
      if (badge.reqStreak && streak >= badge.reqStreak) return true;
      return false;
    });
  }, [longestFast, streak]);

  const recentHistory = useMemo(() => history.slice(0, 5), [history]);

  const fastingOption = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#0f172a',
      textStyle: { color: '#fff', fontSize: 12 },
      borderRadius: 12,
      confine: true
    },
    grid: { left: '2%', right: '2%', bottom: '5%', top: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      data: getLast7DaysData.labels,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#94a3b8', fontWeight: 'bold', fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      splitLine: { show: false },
      axisLabel: { show: false }
    },
    series: [{
      data: getLast7DaysData.values,
      type: 'bar',
      itemStyle: { borderRadius: [6, 6, 6, 6], color: '#3b82f6' },
      barWidth: '40%'
    }]
  }), [getLast7DaysData]);

  const weightOption = useMemo(() => ({
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#0f172a',
      confine: true
    },
    grid: { left: '2%', right: '2%', bottom: '5%', top: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      data: processedWeightData.map(d => d.date),
      axisLine: { show: false },
      axisLabel: { color: '#94a3b8', fontSize: 10 }
    },
    yAxis: {
      type: 'value',
      splitLine: { show: false },
      axisLabel: { show: false }
    },
    series: [{
      data: processedWeightData.map(d => d.value),
      type: 'line',
      smooth: true,
      lineStyle: { width: 3, color: '#f97316' },
      itemStyle: { color: '#f97316' },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(249,115,22,0.15)' },
            { offset: 1, color: 'rgba(249,115,22,0)' }
          ]
        }
      }
    }]
  }), [processedWeightData]);

  return (
    <div className="w-full space-y-6 pb-12">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-slate-100 p-5 rounded-[2rem] shadow-sm">
          <div className="text-slate-400 text-[9px] uppercase font-black tracking-widest mb-1">Current Streak</div>
          <div className="text-3xl font-black text-slate-900 flex items-baseline gap-2">
            {streak} <span className="text-lg">🔥</span>
          </div>
        </div>
        <div className="bg-white border border-slate-100 p-5 rounded-[2rem] shadow-sm">
          <div className="text-slate-400 text-[9px] uppercase font-black tracking-widest mb-1">Best Fast</div>
          <div className="text-3xl font-black text-slate-900 flex items-baseline gap-1">
            {longestFast.toFixed(1)} <span className="text-[10px] text-slate-400 font-bold uppercase">h</span>
          </div>
        </div>
      </div>

      {/* Fasting Chart */}
      <div className="bg-white border border-slate-100 p-6 rounded-[2.5rem] shadow-sm">
        <h3 className="text-slate-900 font-black text-sm mb-4 flex items-center gap-2">
          <SafeIcon icon={FiTrendingUp} className="text-blue-500" /> Fasting Hours
        </h3>
        <Suspense fallback={<ChartSkeleton />}>
          <div className="h-[180px] w-full">
            <ReactECharts 
              option={fastingOption} 
              style={{ height: '100%', width: '100%' }} 
              opts={{ renderer: 'canvas' }}
              lazyUpdate={true}
            />
          </div>
        </Suspense>
      </div>

      {/* Weight Chart - Only if data exists */}
      {weightLogs && weightLogs.length > 1 && (
        <div className="bg-white border border-slate-100 p-6 rounded-[2.5rem] shadow-sm">
          <h3 className="text-slate-900 font-black text-sm mb-4">
            Weight Trend ({units === 'metric' ? 'kg' : 'lb'})
          </h3>
          <Suspense fallback={<ChartSkeleton />}>
            <div className="h-[180px] w-full">
              <ReactECharts 
                option={weightOption} 
                style={{ height: '100%', width: '100%' }} 
                opts={{ renderer: 'canvas' }}
                lazyUpdate={true}
              />
            </div>
          </Suspense>
        </div>
      )}

      {/* Recent Fasts - Optimized */}
      <div>
        <h3 className="text-slate-900 font-black text-lg mb-4 px-2">Recent Fasts</h3>
        <div className="space-y-3">
          {recentHistory.length > 0 ? (
            recentHistory.map((entry, idx) => (
              <HistoryCard 
                key={entry.id || idx} 
                entry={entry} 
                onShare={handleShareHistory}
                safeFormat={safeFormat}
              />
            ))
          ) : (
            <div className="bg-white border border-dashed border-slate-200 rounded-3xl py-10 text-center text-slate-400 text-xs font-bold px-4">
              Your fasting journey starts here. Record your first session!
            </div>
          )}
        </div>
      </div>

      {/* Milestones - Optimized */}
      <div>
        <h3 className="text-slate-900 font-black text-sm mb-4 px-2 flex items-center gap-2">
          <SafeIcon icon={FiAward} className="text-amber-500" /> Milestones
        </h3>
        <div className="grid grid-cols-4 gap-3">
          {BADGES.map(badge => {
            const isEarned = earnedBadges.some(b => b.id === badge.id);
            return (
              <BadgeCard key={badge.id} badge={badge} isEarned={isEarned} />
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Memoized components to prevent unnecessary re-renders
const HistoryCard = React.memo(({ entry, onShare, safeFormat }) => (
  <div className="bg-white border border-slate-100 p-4 rounded-3xl flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className={`p-2.5 rounded-xl ${entry.duration >= entry.target ? 'bg-emerald-50 text-emerald-500' : 'bg-slate-50 text-slate-400'}`}>
        <SafeIcon icon={entry.duration >= entry.target ? FiCheckCircle : FiClock} className="text-lg" />
      </div>
      <div>
        <div className="font-black text-slate-900 text-sm">{entry.duration.toFixed(1)}h Fast</div>
        <div className="text-[9px] font-bold text-slate-400 uppercase">
          {safeFormat(entry.end, 'MMM d, h:mm a')}
        </div>
      </div>
    </div>
    <button 
      onClick={() => onShare(entry)} 
      className="p-2 text-slate-400 hover:text-blue-500 transition-colors active:scale-90"
    >
      <SafeIcon icon={FiShare2} className="text-sm" />
    </button>
  </div>
));

const BadgeCard = React.memo(({ badge, isEarned }) => (
  <div
    onClick={() => isEarned && shareProgress({
      title: 'Earned a Badge!',
      text: `I just earned the ${badge.label} badge on KetoMachine! ${badge.icon} #KetoMachine #Achievement`
    })}
    className={`aspect-square rounded-2xl flex flex-col items-center justify-center p-2 border transition-all duration-300 ${
      isEarned 
        ? 'bg-white border-emerald-100 shadow-sm active:scale-95 cursor-pointer' 
        : 'bg-slate-50 border-slate-100 opacity-30 cursor-default'
    }`}
  >
    <div className={`text-2xl mb-1 ${isEarned ? 'scale-110' : 'grayscale'}`}>{badge.icon}</div>
    <div className="text-[7px] text-center font-black uppercase tracking-tighter text-slate-500 leading-tight">
      {badge.label}
    </div>
  </div>
));